import React, { useState } from 'react';
import { ShoppingBag, Plus, Trash2, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const PublishedWorksCard = ({ publishedWorks, setPublishedWorks, api }) => {
  const [newWork, setNewWork] = useState({ title: '', description: '', cover_image: '', purchase_url: '' });
  const [addingWork, setAddingWork] = useState(false);
  const [showAddWork, setShowAddWork] = useState(false);

  const handleAddWork = async () => {
    if (!newWork.title.trim() || !newWork.purchase_url.trim()) return;
    setAddingWork(true);
    try {
      const res = await api.post('/published-works', newWork);
      setPublishedWorks([res.data, ...publishedWorks]);
      setNewWork({ title: '', description: '', cover_image: '', purchase_url: '' });
      setShowAddWork(false);
    } catch (error) {
      alert('Failed to add published work');
    } finally {
      setAddingWork(false);
    }
  };

  const handleDeleteWork = async (workId) => {
    if (!window.confirm('Remove this published work?')) return;
    try {
      await api.delete(`/published-works/${workId}`);
      setPublishedWorks(publishedWorks.filter(w => w.id !== workId));
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  return (
    <Card className="border-brand-primary/10 mt-6" data-testid="published-works-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShoppingBag className="w-5 h-5 text-brand-primary" />
            Published Works
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => setShowAddWork(!showAddWork)}
            data-testid="add-published-work-btn"
          >
            <Plus className="w-4 h-4" /> Add Book
          </Button>
        </div>
        <p className="text-sm text-text-muted mt-1">Showcase your published books with purchase links for visitors</p>
      </CardHeader>
      <CardContent>
        {showAddWork && (
          <div className="p-4 bg-surface-alt rounded-lg mb-4 space-y-3" data-testid="add-work-form">
            <div className="space-y-2">
              <Label>Book Title *</Label>
              <Input
                value={newWork.title}
                onChange={(e) => setNewWork({ ...newWork, title: e.target.value })}
                placeholder="e.g., My First Novel"
                data-testid="work-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newWork.description}
                onChange={(e) => setNewWork({ ...newWork, description: e.target.value })}
                placeholder="Brief description of your book"
                data-testid="work-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Cover Image URL</Label>
              <Input
                value={newWork.cover_image}
                onChange={(e) => setNewWork({ ...newWork, cover_image: e.target.value })}
                placeholder="https://... (paste image URL)"
                data-testid="work-cover-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Purchase Link *</Label>
              <Input
                value={newWork.purchase_url}
                onChange={(e) => setNewWork({ ...newWork, purchase_url: e.target.value })}
                placeholder="https://amazon.com/..."
                data-testid="work-url-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowAddWork(false)}>Cancel</Button>
              <Button
                size="sm"
                className="btn-primary"
                onClick={handleAddWork}
                disabled={addingWork || !newWork.title.trim() || !newWork.purchase_url.trim()}
                data-testid="save-work-btn"
              >
                {addingWork ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Book'}
              </Button>
            </div>
          </div>
        )}

        {publishedWorks.length === 0 && !showAddWork ? (
          <p className="text-sm text-text-muted text-center py-6">No published works yet. Add your books to showcase them on your profile and the Members page.</p>
        ) : (
          <div className="space-y-3">
            {publishedWorks.map(work => (
              <div key={work.id} className="flex items-center gap-3 p-3 bg-surface-alt rounded-lg" data-testid={`my-work-${work.id}`}>
                {work.cover_image && (
                  <img src={work.cover_image} alt={work.title} className="w-12 h-16 object-cover rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-text-primary text-sm truncate">{work.title}</p>
                  {work.description && <p className="text-xs text-text-muted truncate">{work.description}</p>}
                  <a href={work.purchase_url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-accent hover:underline flex items-center gap-1 mt-0.5">
                    <LinkIcon className="w-3 h-3" /> {work.purchase_url.replace(/^https?:\/\//, '').slice(0, 40)}...
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteWork(work.id)}
                  className="text-text-muted hover:text-red-500"
                  data-testid={`delete-work-${work.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
